import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { type BulkRefreshResult, useBulkRefresh } from '@/lib/bulkRefresh';
import { cn } from '@/lib/cn';

interface Props {
  orgSlug: string;
  partIds: number[];
  onClose: () => void;
  /** Called when the user clicks "Review →" on a needs_review row.
   * Parent closes this modal and opens the part's drawer so the
   * existing validate flow can take over. */
  onOpenPart: (partId: number) => void;
  /** Current drawer part id (or null when no drawer is open). The bulk
   * modal watches transitions from a tracked partId back to null to
   * flip the per-row "reviewed" checkmark — gives the user a visible
   * indication of which rows in `Need review` they've already worked
   * through without leaving the modal. */
  openPartId: number | null;
}

type Phase = 'confirm' | 'progress' | 'results';

/**
 * Bulk-refresh wizard. Three phases inside one modal shell:
 *
 * 1. **Confirm** — count + a short policy summary, big "Start refresh"
 *    button. The user can back out before any adapter call fires.
 * 2. **Progress** — live counter while the hook chunks the part_ids
 *    through `/parts/bulk-refresh` (25 per request). Cancel is best-
 *    effort: the in-flight chunk finishes; subsequent chunks don't
 *    dispatch.
 * 3. **Results** — split by status. "Updated" parts are summarized
 *    (collapsed by default); "Needs review" parts each get an inline
 *    conflict description + a Review link that hands off to the part
 *    drawer (where the user runs the existing per-part validate flow).
 *
 * The bulk endpoint enforces a 25-part-per-request cap; chunking lives
 * in `lib/bulkRefresh.ts` so this component just renders state.
 */
export function BulkRefreshModal({
  orgSlug,
  partIds,
  onClose,
  onOpenPart,
  openPartId,
}: Props) {
  const { state, start, cancel, reset } = useBulkRefresh(orgSlug);
  const [phase, setPhase] = useState<Phase>('confirm');
  const [reviewedIds, setReviewedIds] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  // Watch openPartId transitions: when it returns to null after the user
  // had a needs_review row open, mark that row reviewed. We track the
  // "previous" id in a ref so the effect fires exactly once on the
  // null transition rather than on every render with the same null.
  const prevOpenRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = prevOpenRef.current;
    if (prev !== null && openPartId === null) {
      // Drawer just closed; mark the part as reviewed only if it was
      // one of our needs_review rows. Skips updates when the user
      // opened a part not in this batch (e.g. via a deep link).
      const inBatch = state.results.some(
        (r) => r.part_id === prev && r.status === 'needs_review',
      );
      if (inBatch) {
        setReviewedIds((s) => {
          if (s.has(prev)) return s;
          const next = new Set(s);
          next.add(prev);
          return next;
        });
      }
    }
    prevOpenRef.current = openPartId;
  }, [openPartId, state.results]);

  useEffect(() => {
    if (phase === 'progress' && !state.isRunning && state.total > 0) {
      // Either finished naturally or was cancelled mid-flight.
      setPhase('results');
    }
  }, [phase, state.isRunning, state.total]);

  const tally = useMemo(() => {
    const out = { updated: 0, needs_review: 0, skipped: 0, error: 0 };
    for (const r of state.results) out[r.status] += 1;
    return out;
  }, [state.results]);

  const handleStart = () => {
    setPhase('progress');
    start(partIds);
  };

  const handleCloseAll = () => {
    reset();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-stone-900/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 shadow-xl">
        <div className="flex items-center justify-between border-b border-stone-200 dark:border-stone-800 px-5 py-3">
          <h2 className="text-lg font-semibold">Refresh from supplier</h2>
          <button
            type="button"
            onClick={handleCloseAll}
            className="rounded p-1 text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {phase === 'confirm' && <Confirm count={partIds.length} />}
          {phase === 'progress' && (
            <Progress state={state} onCancel={cancel} />
          )}
          {phase === 'results' && (
            <Results
              tally={tally}
              results={state.results}
              cancelled={state.cancelled}
              error={state.error}
              reviewedIds={reviewedIds}
              // Pass through directly — DO NOT close the bulk modal
              // when the user clicks into a part. The drawer opens
              // *over* this modal at z-40, and closing the drawer
              // returns the user here so they can keep walking
              // through parts that need attention.
              onOpenPart={onOpenPart}
            />
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900 px-5 py-3">
          {phase === 'confirm' && (
            <>
              <Button variant="ghost" onClick={handleCloseAll}>
                Cancel
              </Button>
              <Button onClick={handleStart} disabled={partIds.length === 0}>
                Start refresh ({partIds.length}{' '}
                {partIds.length === 1 ? 'part' : 'parts'})
              </Button>
            </>
          )}
          {phase === 'progress' && (
            <>
              <Button
                variant="ghost"
                onClick={cancel}
                disabled={state.cancelled}
              >
                {state.cancelled ? 'Cancelling…' : 'Cancel'}
              </Button>
            </>
          )}
          {phase === 'results' && (
            <Button onClick={handleCloseAll}>Done</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Confirm({ count }: { count: number }) {
  return (
    <div className="space-y-3 text-sm">
      <p>
        Refresh <strong>{count}</strong> {count === 1 ? 'part' : 'parts'}{' '}
        against {count === 1 ? 'its' : 'their'} primary supplier.
      </p>
      <ul className="list-disc list-inside space-y-1 text-stone-600 dark:text-stone-400">
        <li>
          Supplier-level data (price breaks, MOQ, stock, lead time) is
          always refreshed.
        </li>
        <li>
          Blank Part fields get filled in from the vendor's data —
          values you've already typed are never overwritten.
        </li>
        <li>
          If a vendor disagrees on identity (mfg PN, manufacturer,
          package), the part is flagged for manual review and{' '}
          <em>nothing</em> is changed on it.
        </li>
        <li>
          Parts with no supplier link, or linked to a distributor we
          can't query, are skipped.
        </li>
      </ul>
    </div>
  );
}

function Progress({
  state,
  onCancel,
}: {
  state: ReturnType<typeof useBulkRefresh>['state'];
  onCancel: () => void;
}) {
  const pct = state.total === 0 ? 0 : Math.round((state.done / state.total) * 100);
  void onCancel; // surfaced via footer button; kept here for symmetry
  return (
    <div className="space-y-3 text-sm">
      <p className="flex items-center gap-2">
        <Spinner className="h-4 w-4 text-forge-600 dark:text-forge-400" />
        <span className="font-medium">
          {state.done} of {state.total} processed
        </span>
        <span className="text-stone-500 dark:text-stone-400">({pct}%)</span>
      </p>
      <div className="h-2 overflow-hidden rounded-full bg-stone-100 dark:bg-stone-800">
        <div
          className="h-full bg-forge-500 transition-[width] duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
      <ResultTally tally={tallyOf(state.results)} />
      {state.error && (
        <p className="text-sm text-red-600">
          Stopped after error: {state.error.message}
        </p>
      )}
    </div>
  );
}

function tallyOf(results: BulkRefreshResult[]) {
  const out = { updated: 0, needs_review: 0, skipped: 0, error: 0 };
  for (const r of results) out[r.status] += 1;
  return out;
}

function ResultTally({
  tally,
}: {
  tally: { updated: number; needs_review: number; skipped: number; error: number };
}) {
  const items = [
    { key: 'updated', label: 'updated', cls: 'text-emerald-700 dark:text-emerald-400' },
    { key: 'needs_review', label: 'need review', cls: 'text-amber-700 dark:text-amber-400' },
    { key: 'skipped', label: 'skipped', cls: 'text-stone-500 dark:text-stone-400' },
    { key: 'error', label: 'errored', cls: 'text-red-600' },
  ] as const;
  return (
    <p className="text-xs text-stone-600 dark:text-stone-400">
      {items.map((it, i) => (
        <span key={it.key}>
          {i > 0 && <span className="mx-2">·</span>}
          <span className={cn('font-medium', it.cls)}>
            {tally[it.key]}
          </span>{' '}
          {it.label}
        </span>
      ))}
    </p>
  );
}

function Results({
  tally,
  results,
  cancelled,
  error,
  reviewedIds,
  onOpenPart,
}: {
  tally: { updated: number; needs_review: number; skipped: number; error: number };
  results: BulkRefreshResult[];
  cancelled: boolean;
  error: Error | null;
  reviewedIds: ReadonlySet<number>;
  onOpenPart: (partId: number) => void;
}) {
  const updated = results.filter((r) => r.status === 'updated');
  const review = results.filter((r) => r.status === 'needs_review');
  const skipped = results.filter((r) => r.status === 'skipped');
  const errored = results.filter((r) => r.status === 'error');

  return (
    <div className="space-y-4 text-sm">
      <ResultTally tally={tally} />
      {cancelled && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          Cancelled mid-way — {results.length} parts processed before stop.
        </p>
      )}
      {error && (
        <p className="text-xs text-red-600">
          Stopped after error: {error.message}
        </p>
      )}

      {review.length > 0 && (
        <Section title={`Need review (${review.length})`} tone="amber" defaultOpen>
          <p className="mb-2 text-xs text-stone-600 dark:text-stone-400">
            The vendor disagreed on an identity column (mfg PN,
            manufacturer, or package). Open each part to review.
          </p>
          <ul className="space-y-2">
            {review.map((r) => {
              const reviewed = reviewedIds.has(r.part_id);
              return (
                <li
                  key={r.part_id}
                  className={cn(
                    'rounded border px-3 py-2',
                    reviewed
                      ? 'border-emerald-200 dark:border-emerald-800/60 bg-emerald-50/60 dark:bg-emerald-900/10 opacity-80'
                      : 'border-amber-200 dark:border-amber-800/60 bg-amber-50/60 dark:bg-amber-900/10',
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 font-mono text-xs text-stone-600 dark:text-stone-400">
                      {reviewed && (
                        <span
                          className="text-sm font-semibold text-emerald-700 dark:text-emerald-400"
                          aria-label="Reviewed"
                          title="You've opened this part — closing the drawer flipped it to reviewed."
                        >
                          ✓
                        </span>
                      )}
                      <span className={reviewed ? 'line-through' : ''}>
                        {r.part_number ?? `Part ${r.part_id}`}
                      </span>
                      <span className="text-stone-400 dark:text-stone-500">·</span>
                      <span>{r.distributor_name}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => onOpenPart(r.part_id)}
                      className="text-xs text-forge-700 dark:text-forge-400 hover:underline"
                    >
                      {reviewed ? 'Open again →' : 'Review →'}
                    </button>
                  </div>
                  <ul className="mt-1 space-y-0.5 text-xs text-stone-700 dark:text-stone-300">
                    {r.conflicts.map((c) => (
                      <li key={c.field}>
                        <span className="font-medium">{c.field}:</span>{' '}
                        stored <code className="font-mono">{formatVal(c.current_value)}</code>{' '}
                        vs vendor{' '}
                        <code className="font-mono">{formatVal(c.vendor_value)}</code>
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      {errored.length > 0 && (
        <Section title={`Errored (${errored.length})`} tone="red" defaultOpen>
          <p className="mb-2 text-xs text-stone-600 dark:text-stone-400">
            The adapter call failed for these parts. "Not found"
            usually means the stored distributor PN is stale — open
            the part, check the supplier's website, and update or
            mark the part EOL.
          </p>
          <ul className="space-y-2">
            {errored.map((r) => (
              <li
                key={r.part_id}
                className="rounded border border-red-200 dark:border-red-800/60 bg-red-50/60 dark:bg-red-900/15 px-3 py-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs text-stone-700 dark:text-stone-300">
                    {r.part_number ?? `Part ${r.part_id}`}
                    {r.distributor_name && ` · ${r.distributor_name}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => onOpenPart(r.part_id)}
                    className="text-xs text-forge-700 dark:text-forge-400 hover:underline"
                  >
                    Open part →
                  </button>
                </div>
                <p className="mt-1 text-xs text-red-800 dark:text-red-300">
                  {r.error_message}
                </p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {updated.length > 0 && (
        <Section title={`Updated (${updated.length})`} tone="emerald">
          <p className="text-xs text-stone-600 dark:text-stone-400">
            {updated.filter((r) => r.applied_fields.length > 0).length}{' '}
            had blank fields filled in; the rest only refreshed
            supplier-level data (price, stock, lead time).
          </p>
        </Section>
      )}

      {skipped.length > 0 && (
        <Section title={`Skipped (${skipped.length})`} tone="stone">
          <ul className="space-y-1 text-xs text-stone-600 dark:text-stone-400">
            {skipped.map((r) => (
              <li key={r.part_id}>
                <span className="font-mono">
                  {r.part_number ?? `Part ${r.part_id}`}
                </span>{' '}
                — {humanReason(r.skip_reason)}
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

function humanReason(reason: string | null): string {
  switch (reason) {
    case 'no_supplier':
      return 'no supplier linked';
    case 'no_adapter':
      return 'no adapter is wired up for this distributor';
    case 'supplier_disabled':
      return 'distributor is not enabled in this org';
    case 'part_not_found':
      return 'part not found';
    case 'part_obsolete':
      return 'part is marked obsolete';
    default:
      return reason ?? 'unknown';
  }
}

function formatVal(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

function Section({
  title,
  tone,
  defaultOpen = false,
  children,
}: {
  title: string;
  tone: 'amber' | 'red' | 'emerald' | 'stone';
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const toneClass = {
    amber: 'text-amber-800 dark:text-amber-400',
    red: 'text-red-700 dark:text-red-400',
    emerald: 'text-emerald-800 dark:text-emerald-400',
    stone: 'text-stone-700 dark:text-stone-300',
  }[tone];
  return (
    <div className="rounded-md border border-stone-200 dark:border-stone-800">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium',
          toneClass,
        )}
      >
        <span>{title}</span>
        <span className="text-xs">{open ? '▾' : '▸'}</span>
      </button>
      {open && <div className="border-t border-stone-200 dark:border-stone-800 px-3 py-2">{children}</div>}
    </div>
  );
}
